import React, { useEffect, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Stack from 'react-bootstrap/Stack';
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const AssetScenarioPlanningCharts = (param) => {

	const [loading, setLoading] = useState(true);
	const [asset, setAsset] = useState(param.asset);
	const [chart1Data, setChart1Data] = useState(null);
    const [chart2Data, setChart2Data] = useState(null);
	const [chart1Labels, setChart1Labels] = useState(null);
    const [chart2Labels, setChart2Labels] = useState(null);
	const [assetScenarioRequirements, setAssetScenerioRequirements] = useState([]);

	// Add a new data structure here to contain the modified
	// charting data - multiple values per model year entry

	// Modify useEffect below (or another one) to re-populate
	// the charting data on change in asset

	useEffect(() => {
		var data1 = [];
	    var data2 = [];
		var labels1 = [];
	    var labels2 = [];

		// Scenario - the current model in the spending plan
		// asset.scenarios[0] - no spending scenario
		// asset.scenarios [1...] - additional scenarios

		var selectedScenarios = param.asset.scenarios.filter(scenario => scenario.id === param.asset.scenarioID || scenario.id === 0);
		param.asset.scenarios[0].scenarioYears.forEach((y, yearIndex) => {
			let entry = {};
			param.asset.scenarios.forEach((scenario, scenarioIndex) => {
				entry['year'] = y.year;
				entry[scenario.name] = scenario.scenarioYears[yearIndex].fci;
			});

			data1.push(entry);

			let entry2 = {};
			selectedScenarios.forEach((scenario, scenarioIndex) => {
				entry2['year'] = y.year;
				entry2[scenario.name] = scenario.scenarioYears[yearIndex].fci;
			});

			data2.push(entry2);
		});

		param.asset.scenarios.forEach((scenario, scenarioIndex) => {
			labels1.push(scenario.name);
			if (param.asset.scenarioID === scenario.id || scenario.id === 0) {
				labels2.push(scenario.name);
			}
		});

		setChart1Data(data1);
		setChart1Labels(labels1);

		setChart2Data(data2);
		setChart2Labels(labels2);

		const _assetScenarioRequirements = [];
		param.asset.scenarioRequirements.forEach((scenarioRequirement, i) => {
			const item = _assetScenarioRequirements.find(q => q.system === scenarioRequirement.system);
			if (item !== undefined) {
				item.cost += scenarioRequirement.cost;
			}
			else {
				_assetScenarioRequirements.push({ system: scenarioRequirement.system, cost: scenarioRequirement.cost });
			}
		});
		setAssetScenerioRequirements(_assetScenarioRequirements);

		setLoading(false);
	}, [loading, param]);

	const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#B4009E', '#61D6D6', '#767676', '#7F3300', '#FF7F7F', '#7FC9FF', '#000000', '#D67FFF'];

	const RADIAN = Math.PI / 180;
	const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
		const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
		const x = cx + radius * Math.cos(-midAngle * RADIAN);
		const y = cy + radius * Math.sin(-midAngle * RADIAN);

		if (percent === 0) {
			return null;
		}

		return (
			<text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
				{`${(percent * 100).toFixed(0)}%`}
			</text>
		);
	};

	return loading ? (
		<p><em>Loading...</em></p>
	) : (
		<React.Fragment>
			<Container>
				<Row>
					<Col sm={7}>
						<Row>
							<h6>{param.asset.scenarioID !== 0 ? "Selected Scenario vs. Baseline - No Investment" : "Selected Scenario"}</h6>
							<ResponsiveContainer width="100%" height={450}  >
								<LineChart data={chart2Data} margin={{ top: 0, left: 5, right: 5, bottom: 100 }}>
									{chart2Labels.map((entry, index) =>
										<Line type="monotone" key={index} dataKey={entry} stroke={COLORS[index % COLORS.length]} />
									)}
									<CartesianGrid stroke="#ccc" />
									<XAxis dataKey="year"
										label={{
											value: 'Fiscal Year',
											style: { textAnchor: 'middle' },
											angle: 0,
											position: 'bottom',
											offset: 20,
										}}
										tick={{
											angle: -90,
										}}
										tickMargin={20}
									/>
									<YAxis label={{
										value: 'FCI',
										style: { textAnchor: 'middle' },
										angle: -90,
										position: 'left',
										offset: 0,
									}}
									/>
									<Legend verticalAlign="bottom" wrapperStyle={{ position: 'relative' }} height={35} />
								</LineChart>
							</ResponsiveContainer>
						</Row>
						<Row>
							<h6>All Scenarios</h6>
							<ResponsiveContainer width="100%" height={450} >
								<LineChart data={chart1Data} margin={{ top: 0, left: 5, right: 5, bottom: 100 }}>
									{chart1Labels.map((entry, index) =>
										<Line type="monotone" key={index} dataKey={entry} stroke={COLORS[index % COLORS.length]} />
									)}
									<CartesianGrid stroke="#ccc" />
									<XAxis dataKey="year"
										label={{
											value: 'Fiscal Year',
											style: { textAnchor: 'middle' },
											angle: 0,
											position: 'bottom',
											offset: 20,
										}}
										tick={{
											angle: -90,
										}}
										tickMargin={20}
									/>
									<YAxis label={{
										value: 'FCI',
										style: { textAnchor: 'middle' },
										angle: -90,
										position: 'left',
										offset: 0,
									}}
									/>
									<Legend verticalAlign="bottom" wrapperStyle={{ position: 'relative' }} height={35} />
								</LineChart>
							</ResponsiveContainer>
						</Row>
					</Col>
					<Col sm={5}>
						<h6 style={{ marginLeft: '50px' }}>Requirements</h6>
						<ResponsiveContainer width="100%" height={500}  >
							<PieChart>
								<Pie
										data={assetScenarioRequirements}
									cx="50%"
									cy="50%"
									labelLine={false}
									label={renderCustomizedLabel}
									outerRadius={80}
									fill="#8884d8"
									dataKey="cost"
									name="system"
								>
										{assetScenarioRequirements.map((entry, index) => (
										<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
									))}
								</Pie>
								<Legend layout="vertical" payload={
										assetScenarioRequirements.map(
										(item, index) => ({
											id: item.id,
											color: COLORS[index % COLORS.length],
											value: item.system,
										})
									)
								} />
							</PieChart>
						</ResponsiveContainer>

					</Col>
				</Row>
			</Container>
		</React.Fragment>
	);
}

export default AssetScenarioPlanningCharts;